import { GearSixIcon, QuestionIcon, WarningCircleIcon, WarningIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import { useRef } from 'react'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import { GhostButton } from '../../../components/buttons/ghost-button.tsx'
import { SystemTag } from '../../../components/tags/system-tag.tsx'
import { TooltipWithHover } from '../../../components/tooltip.tsx'
import type { Integration } from '../../../utils/api-client/api-schemas.ts'
import { IntegrationBrandIcon } from './integration-icon.tsx'
import {
  getInstanceFailureDetail,
  getInstanceHealth,
  getInstanceName,
  getIsNeedsAttention,
  getIsSystemInstance,
  getProviderLabel,
  type InstanceHealth,
} from './integration-instance-utils.ts'
import * as styles from './integrations-tab.css.ts'

type InstanceCardProps = {
  integration: Integration
  onManage: (opener: HTMLElement) => unknown
  /** Whether this instance's Manage drawer is open. */
  isSelected?: boolean
}

export const InstanceCard: React.FC<InstanceCardProps> = ({ integration, onManage, isSelected = false }) => {
  const health = getInstanceHealth(integration)
  const isSystem = getIsSystemInstance(integration)
  const failureDetail = getInstanceFailureDetail(integration)
  // Shared with the tab's banner count, so a card that draws itself as healthy is never one of the
  // instances the banner above it says need attention. See `getIsNeedsAttention`.
  const isNeedsAttention = getIsNeedsAttention(integration)
  const manageButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <div
      className={classNames(styles.instanceCard, {
        [styles.instanceCardUnhealthy]: isNeedsAttention,
        [styles.instanceCardInteractive]: !isSystem,
        [styles.instanceCardSelected]: isSelected && !isSystem,
      })}
      data-testid="instance-card"
    >
      <div className={styles.instanceCardHeader}>
        <InstanceBranding integration={integration} className={styles.instanceCardBranding} />
        {!isSystem && <HealthDot health={health} failureDetail={failureDetail} className={styles.instanceCardRaised} />}
      </div>

      {isNeedsAttention && failureDetail && <p className={styles.instanceCardFailureDetail}>{failureDetail}</p>}

      <div className={styles.instanceCardFooter}>
        {isSystem ? (
          <SystemInstanceNote />
        ) : isNeedsAttention ? (
          // Filled to draw the eye when the instance needs attention, quieter when it does not.
          <DefaultButton
            ref={manageButtonRef}
            buttonType="secondary"
            size="small"
            onClick={event => onManage(event.currentTarget)}
          >
            <span className={styles.manageButtonContent}>
              {/* A failed check names its reason, so it reads as an error; without one it is only a warning. */}
              {failureDetail ? (
                <WarningCircleIcon size={16} className={styles.failureIcon} data-testid="manage-failure-icon" />
              ) : (
                <WarningIcon size={16} className={styles.needsAttentionIcon} data-testid="manage-warning-icon" />
              )}
              Manage
            </span>
          </DefaultButton>
        ) : (
          <GhostButton
            ref={manageButtonRef}
            type="secondary"
            size="small"
            onClick={event => onManage(event.currentTarget)}
            icon={<GearSixIcon size={16} />}
          >
            Manage
          </GhostButton>
        )}
      </div>

      {/* A mouse shortcut to Manage, so it is hidden from assistive tech and the tab order: the Manage
          button is the accessible way in, and it is what focus returns to when the drawer closes. */}
      {!isSystem && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          className={styles.instanceCardHitArea}
          onClick={() => manageButtonRef.current && onManage(manageButtonRef.current)}
        />
      )}
    </div>
  )
}

/**
 * The footer of a card for an instance this tab cannot edit.
 *
 * A system instance comes from the deployment's own configuration, so there is no Manage action to
 * offer. The badge says what it is and the tooltip says where to change it, which is the only
 * question a reader has on seeing a card with no button on it.
 */
const SystemInstanceNote: React.FC = () => (
  <span className={styles.systemInstanceNote}>
    <SystemTag />
    <TooltipWithHover
      trigger={
        <span className={styles.systemInstanceHelpIcon} role="img" aria-label="Why can this not be edited?">
          <QuestionIcon size={16} weight="regular" />
        </span>
      }
    >
      Configured in the Pixee Enterprise Server admin dashboard. Edit it there.
    </TooltipWithHover>
  </span>
)

type HealthDotProps = {
  health: InstanceHealth
  failureDetail?: string
  className?: string
}

export const HealthDot: React.FC<HealthDotProps> = ({ health, failureDetail, className }) => {
  const dotClassName = match(health)
    .with('healthy', () => styles.healthDotHealthy)
    .with('needs-attention', () => styles.healthDotNeedsAttention)
    .with('unknown', () => styles.healthDotUnknown)
    .exhaustive()
  const statusLabel = match(health)
    .with('healthy', () => 'Healthy')
    .with('needs-attention', () => 'Needs attention')
    .with('unknown', () => 'Unknown')
    .exhaustive()
  // The detail is the actionable half of the status, so it belongs in the accessible name too — a
  // screen-reader user should not have to open the drawer to learn why an instance is unhealthy.
  const label = failureDetail ? `Status: ${statusLabel}. ${failureDetail}` : `Status: ${statusLabel}`
  return (
    <TooltipWithHover
      trigger={<span className={classNames(styles.healthDot, dotClassName, className)} role="img" aria-label={label} />}
    >
      {label}
    </TooltipWithHover>
  )
}

type InstanceBrandingProps = {
  integration: Integration
  className?: string
}

/**
 * The icon + tool name + instance name block, shared by both cards so a tweak to it lands once. The
 * tool identifies the card (first line, matching the brand logo); the user's instance name is the
 * second-line subheader, which is what tells two instances of the same tool apart.
 */
const InstanceBranding: React.FC<InstanceBrandingProps> = ({ integration, className }) => {
  const instanceName = getInstanceName(integration)

  return (
    <div className={className}>
      <span className={styles.instanceCardIcon}>
        <IntegrationBrandIcon integration={integration} />
      </span>
      <div>
        <p className={styles.instanceCardName}>{getProviderLabel(integration.type)}</p>
        {instanceName && <p className={styles.instanceCardSubLabel}>{instanceName}</p>}
      </div>
    </div>
  )
}

type ReadOnlyInstanceCardProps = {
  integration: Integration
}

/**
 * Shown when in-product integration management is not opted in: the tool, the instance name, and
 * nothing to act on — no health dot and no Manage action.
 *
 * A system instance still says so. On an installation that never opted in, every instance in this
 * list is one the Enterprise Server admin console owns, and the badge is what says where to go to
 * change it. A managed instance here — an installation that opted out after configuring some — gets
 * no badge, because the admin console is not where it lives.
 */
export const ReadOnlyInstanceCard: React.FC<ReadOnlyInstanceCardProps> = ({ integration }) => {
  return (
    <div className={styles.readOnlyCard} data-testid="read-only-instance-card">
      <InstanceBranding integration={integration} className={styles.instanceCardBranding} />
      {getIsSystemInstance(integration) && <SystemTag title="Configured in the admin console" />}
    </div>
  )
}
